import {
  parseCommitMessage,
  pullRequestUrl,
  commitUrl,
  jiraTicketUrl,
  plural,
} from "../ReleaseView/releaseViewHelpers";

// Tags are heterogeneous — never normalise, sort as numeric-aware strings.
const byTagDesc = (a, b) =>
  String(b || "").localeCompare(String(a || ""), undefined, { numeric: true });

// Patches newest-first: compare by label using the same numeric-aware collation.
const byPatchDesc = (a, b) =>
  String(b.patchLabel || "").localeCompare(
    String(a.patchLabel || ""),
    undefined,
    { numeric: true }
  );

/**
 * Classify the double-commit flag for a raw commit. A commit is a double commit
 * iff the backend says so via is_double_commit; the "Double Commit:" message
 * prefix is only stripped for display, never used to classify.
 * - "double" — is_double_commit is true
 * - "nodc"   — NODC prefix present, not a double
 * - "none"   — clean commit
 */
const flagKindOf = (isDoubleField, nodc) => {
  if (isDoubleField) return "double";
  if (nodc) return "nodc";
  return "none";
};

/**
 * Deduplicate commits within a patch by hash, keeping the first occurrence and
 * recording the repetition count (>1 may signal a cherry-pick).
 */
const dedupeByHash = (rawCommits) => {
  const seen = new Map();
  rawCommits.forEach((commit) => {
    const hash = commit?.commit_hash || "";
    if (!seen.has(hash)) {
      seen.set(hash, { commit, count: 1 });
    } else {
      seen.get(hash).count += 1;
    }
  });
  return Array.from(seen.values());
};

/**
 * Flatten the service-view API response into an array of service groups.
 * Structure: response[] → repo_name / releases[] → release_label / patches[] → patch_label / commits[]
 */
export const adaptServiceView = (response = []) =>
  (response || [])
    .map((repo) => {
      const repoName = repo?.repo_name || "";
      const rows = [];

      (repo?.releases || []).forEach((release) => {
        const releaseLabel = release?.release_label || "";
        (release?.patches || []).forEach((patch) => {
          const patchLabel = patch?.patch_label || "";
          const deduped = dedupeByHash(patch?.commits || []);

          deduped.forEach(({ commit, count }) => {
            const raw = commit?.commit_message || "";
            const { text, nodc, pr } = parseCommitMessage(raw);
            const flagKind = flagKindOf(
              Boolean(commit?.is_double_commit),
              nodc
            );

            rows.push({
              id: `${repoName}:${releaseLabel}:${patchLabel}:${
                commit?.commit_hash || ""
              }`,
              repoName,
              releaseLabel,
              patchLabel,
              tag: commit?.commit_tag || "",
              jiraId: commit?.jira_id || "",
              hash: commit?.commit_hash || "",
              message: text,
              nodc,
              pr,
              flagKind,
              // isDouble drives the Double/Original segment split
              isDouble: flagKind === "double",
              // repeatCount > 1 flags possible cherry-pick
              repeatCount: count,
            });
          });
        });
      });

      // Sort: patch newest-first, then tag desc within patch
      rows.sort((a, b) => byPatchDesc(a, b) || byTagDesc(a.tag, b.tag));

      const commits = rows;
      const releases = Array.from(new Set(rows.map((r) => r.releaseLabel)));
      const patches = Array.from(new Set(rows.map((r) => r.patchLabel)));
      const doubles = rows.filter((r) => r.isDouble).length;

      return {
        key: repoName,
        repoName,
        commits,
        releases,
        patches,
        doubles,
      };
    })
    .sort((a, b) => a.repoName.localeCompare(b.repoName));

/** @param {any[]} commits */
export const serviceSegmentCounts = (commits = []) => ({
  all: commits.length,
  double: commits.filter((c) => c.isDouble).length,
  original: commits.filter((c) => !c.isDouble).length,
  noTicket: commits.filter((c) => !c.jiraId).length,
});

/** @param {any} commit @param {string} segment */
export const serviceMatchesSegment = (commit, segment) => {
  if (segment === "double") return commit.isDouble;
  if (segment === "original") return !commit.isDouble;
  if (segment === "noTicket") return !commit.jiraId;
  return true;
};

/** @param {any} commit @param {string} query already lower-cased */
export const serviceMatchesQuery = (commit, query) =>
  !query ||
  [
    commit.message,
    commit.jiraId,
    commit.repoName,
    commit.hash,
    commit.releaseLabel,
    commit.patchLabel,
  ].some((f) => (f || "").toLowerCase().includes(query));

export { pullRequestUrl, commitUrl, jiraTicketUrl, plural };

/** Tag range across commits */
export const serviceTagRange = (commits = []) => {
  const tags = commits
    .map((c) => c.tag)
    .filter(Boolean)
    .sort(byTagDesc);
  if (!tags.length) return "";
  const [hi] = tags;
  const lo = tags[tags.length - 1];
  return hi === lo ? hi : `${lo} \u2013 ${hi}`;
};
